import { Actor, type Identity } from "@icp-sdk/core/agent";
import { createHttpAgent } from "@motokomaker/ic-client";
import { useMutation, useQuery } from "@tanstack/react-query";
import { idlFactory } from "@/declarations/protocol_assistant.did.js";
import type {
	ChatMessage,
	ChatResult,
	_SERVICE,
} from "@/declarations/protocol_assistant.did.d.ts";
import { useAuth } from "@/lib/auth";
import { getIcConfig } from "@/lib/network";

export const PROTOCOL_ASSISTANT_CANISTER_ID =
	import.meta.env.VITE_PROTOCOL_ASSISTANT_CANISTER_ID ??
	import.meta.env.VITE_CANISTER_ID_PROTOCOL_ASSISTANT ??
	"";

export type UiChatMessage = {
	id: string;
	role: "user" | "assistant";
	content: string;
};

function formatChatError(result: ChatResult): string {
	if ("ok" in result) return result.ok;
	if ("RateLimited" in result.err) {
		return "Daily question limit reached. Try again tomorrow.";
	}
	if ("InvalidInput" in result.err) return result.err.InvalidInput;
	if ("LlmFailed" in result.err) return result.err.LlmFailed;
	return "Assistant unavailable.";
}

/** Strip pseudo tool-call lines models sometimes emit as plain text. */
export function sanitizeAssistantText(text: string): string {
	return text
		.split("\n")
		.filter((line) => {
			const t = line.toLowerCase();
			if (t.includes("/search_docs")) return false;
			if (t.includes("/protocol_health")) return false;
			if (t.includes("/price_feeds")) return false;
			if (t.includes("tool_call")) return false;
			if (t.includes('{"keywords"') && t.includes("search")) return false;
			if (t.trim().startsWith("</function")) return false;
			return true;
		})
		.join("\n")
		.trim();
}

export function formatAssistantTransportError(err: unknown): string {
	const msg = err instanceof Error ? err.message : String(err ?? "Unknown error");
	const low = msg.toLowerCase();
	if (
		low.includes("ic0406") ||
		low.includes("out of cycles") ||
		low.includes("insufficient cycles") ||
		low.includes("grow memory")
	) {
		return "Assistant is temporarily low on cycles or under load. Please try again in a moment.";
	}
	if (low.includes("rate") && low.includes("limit")) {
		return "Daily question limit reached. Try again tomorrow or sign in.";
	}
	return msg;
}

async function createAssistantActor(identity?: Identity | null) {
	if (!PROTOCOL_ASSISTANT_CANISTER_ID) return null;
	const config = getIcConfig();
	const agent = await createHttpAgent(config, identity ?? undefined);
	return Actor.createActor<_SERVICE>(idlFactory, {
		agent,
		canisterId: PROTOCOL_ASSISTANT_CANISTER_ID,
	});
}

export function useProtocolAssistantEnabled() {
	return Boolean(PROTOCOL_ASSISTANT_CANISTER_ID);
}

export function useProtocolAssistantHealth() {
	return useQuery({
		queryKey: ["protocol-assistant", "health", PROTOCOL_ASSISTANT_CANISTER_ID],
		enabled: Boolean(PROTOCOL_ASSISTANT_CANISTER_ID),
		queryFn: async () => {
			const actor = await createAssistantActor();
			if (!actor) return null;
			return actor.health();
		},
		staleTime: 60_000,
	});
}

export function useProtocolAssistantChat() {
	const { identity } = useAuth();

	return useMutation({
		mutationFn: async (messages: UiChatMessage[]): Promise<string> => {
			if (!PROTOCOL_ASSISTANT_CANISTER_ID) {
				throw new Error(
					"Protocol Assistant canister is not configured for this environment. Set VITE_PROTOCOL_ASSISTANT_CANISTER_ID in .env.local.",
				);
			}
			const actor = await createAssistantActor(identity ?? null);
			if (!actor) {
				throw new Error("Protocol Assistant canister is not configured.");
			}
			const payload: ChatMessage[] = messages.map((m) => ({
				role: m.role === "user" ? { user: null } : { assistant: null },
				content: m.content,
			}));
			const result = await actor.chat(payload);
			const rec = result as Record<string, unknown>;
			const okText =
				typeof rec.ok === "string"
					? rec.ok
					: typeof rec.Ok === "string"
						? rec.Ok
						: null;
			if (okText != null) {
				const cleaned = sanitizeAssistantText(okText);
				return cleaned.length > 0
					? cleaned
					: "I could not form a clean answer. Try rephrasing, or open Docs.";
			}
			throw new Error(formatChatError(result));
		},
	});
}
